import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import { trackCTA } from "../../lib/analytics";
import { buildDemoInvitationUrl } from "../../lib/demoInvitation.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Page hero. Same shape as the three sibling heroes — eyebrow, H1 at
 * `heading-9vw`, supporting line, two actions — so the four SEO pages read as
 * chapters of one story rather than four designs.
 *
 * The secondary action is the sample invitation, not the sandbox. An RSVP is
 * something a GUEST does, and the only place a visitor can do it is the
 * reserved demo invitation, whose RSVP renders a real attendance matrix and
 * answers the submit with `demo: true` without writing anything
 * (api/constants/demoEvent.mjs, api/routes/rsvp.routes.mjs). `/try` provisions
 * a couple-side Heirloom workspace — the right offer for /wedding-planning and
 * the wrong one here, so this page does not link it at all.
 *
 * `trackLead` deliberately does NOT fire alongside `trackCTA`: unlike `/try`,
 * opening the sample invitation provisions nothing. This is exactly how
 * /digital-wedding-invitation treats the identical URL.
 *
 * No `data-w-id` and no inline `opacity: 0`, for the reason documented on the
 * photo-sharing hero: the H1 must never depend on the Webflow interaction
 * engine to become visible.
 */
const RsvpHero = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  return (
    <header className="section wps-hero">
      <div className="container">
        <p className="wps-eyebrow">{t("weddingRsvp.hero.eyebrow")}</p>
        <h1 className="heading-9vw wps-hero-heading">
          {t("weddingRsvp.hero.heading")}
        </h1>
        <p className="body-20 wps-hero-body">{t("weddingRsvp.hero.body")}</p>
        <div className="wps-hero-actions">
          <Button
            href={localize("/#pricing")}
            text={t("weddingRsvp.hero.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("wedding_rsvp_hero", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <a
            className="wps-text-link"
            href={buildDemoInvitationUrl(shortLanguage)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackCTA("wedding_rsvp_hero", {
                ...analyticsContext,
                ctaName: "view_demo",
              })
            }
          >
            {t("weddingRsvp.hero.ctaSecondary")}
          </a>
        </div>
      </div>
    </header>
  );
};

export default RsvpHero;
